import { Editor, getKeyboardShortcuts, marks, nodes } from "ngx-editor";
import { keymap } from "prosemirror-keymap";
import {
  Fragment,
  Node as ProseMirrorNode,
  NodeSpec,
  ResolvedPos,
  Schema
} from "prosemirror-model";
import { canSplit, insertPoint } from "prosemirror-transform";

// ngx-editor bundles its own prosemirror-state/view, so type against its
// copies rather than the root packages to keep the editor's types compatible.
type EditorView = Editor["view"];
type EditorState = EditorView["state"];
type Transaction = EditorState["tr"];
type Selection = Transaction["selection"];
type Plugin = EditorState["plugins"][number];
type Dispatch = (tr: Transaction) => void;

interface SelectionClass {
  near($pos: ResolvedPos, bias?: number): Selection;
  fromJSON(
    doc: ProseMirrorNode,
    json: { type: "text" | "node"; anchor: number; head?: number }
  ): Selection;
}

// New selections must come from the same prosemirror-state copy the editor
// runs on: its view checks `instanceof NodeSelection` to highlight a selected
// accordion, which fails for selections built from the root package. Every
// selection class extends that copy's Selection, so take it from the state.
function selectionClass(state: EditorState): SelectionClass {
  return Object.getPrototypeOf(state.selection.constructor);
}

function textSelection(state: EditorState, doc: ProseMirrorNode, anchor: number, head = anchor): Selection {
  return selectionClass(state).fromJSON(doc, { type: "text", anchor, head });
}

function nearSelection(state: EditorState, $pos: ResolvedPos, bias = 1): Selection {
  return selectionClass(state).near($pos, bias);
}

const ACCORDION_TITLE = "Accordion title";

// <details><summary>Title</summary>...blocks</details>
const details: NodeSpec = {
  content: "details_summary block+",
  group: "block",
  defining: true,
  attrs: {
    open: { default: false }
  },
  parseDOM: [
    {
      tag: "details",
      getAttrs: (dom: HTMLElement) => ({ open: dom.hasAttribute("open") })
    }
  ],
  toDOM: (node) => ["details", node.attrs["open"] ? { open: "" } : {}, 0]
};

const detailsSummary: NodeSpec = {
  content: "inline*",
  defining: true,
  parseDOM: [{ tag: "summary" }],
  toDOM: () => ["summary", 0]
};

// ngx-editor's default schema drops unknown tags, so <details>/<summary> must
// be declared for them to survive both editing and loading saved descriptions.
export const accordionSchema = new Schema({
  nodes: {
    ...nodes,
    details,
    details_summary: detailsSummary
  },
  marks
});

// Keeps accordions expanded while editing and stops a click on the summary
// from collapsing the body, so the content stays reachable. The saved HTML
// still uses the node's own `open` attribute.
class DetailsView {
  dom: HTMLElement;
  contentDOM: HTMLElement;

  constructor() {
    this.dom = this.contentDOM = document.createElement("details");
    this.dom.setAttribute("open", "");
    this.dom.addEventListener("click", (event) => {
      if ((event.target as HTMLElement).closest("summary")) {
        event.preventDefault();
      }
    });
  }

  update(node: ProseMirrorNode): boolean {
    return node.type.name === "details";
  }

  ignoreMutation(mutation: { type: string }): boolean {
    return mutation.type === "attributes";
  }
}

const accordionNodeViews = {
  details: () => new DetailsView()
};

interface FoundAccordion {
  node: ProseMirrorNode;
  pos: number;
}

// The innermost accordion around the selection, or the accordion itself when
// it is node-selected.
function findAccordion(state: EditorState): FoundAccordion | null {
  const { selection } = state;
  const selected = (selection as { node?: ProseMirrorNode }).node;
  if (selected?.type.name === "details") {
    return { node: selected, pos: selection.from };
  }

  const { $from } = selection;
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth);
    if (node.type.name === "details") {
      return { node, pos: $from.before(depth) };
    }
  }
  return null;
}

export const isInAccordion = (state: EditorState): boolean =>
  !!findAccordion(state);

const isEmptyTextblock = (node: ProseMirrorNode): boolean =>
  node.isTextblock && node.content.size === 0;

// Removes the accordion but keeps its content: the title becomes a paragraph
// and the body blocks move up a level. Blank lines are dropped, so an empty
// accordion disappears entirely. The cursor stays on the text it was in.
function unwrapAccordion(
  state: EditorState,
  dispatch: Dispatch | undefined,
  { node, pos }: FoundAccordion
): boolean {
  const { paragraph } = state.schema.nodes;
  const cursor = state.selection.from;
  const blocks: ProseMirrorNode[] = [];
  let newPos = pos;
  let target: number | null = null;

  // Keeps a block, noting where the cursor lands if it was inside it. The
  // summary and its replacement paragraph have the same structure, so offsets
  // inside them line up one-to-one.
  const keep = (block: ProseMirrorNode, oldStart: number, oldSize: number) => {
    if (cursor > oldStart && cursor < oldStart + oldSize) {
      target = newPos + (cursor - oldStart);
    }
    blocks.push(block);
    newPos += block.nodeSize;
  };

  let childStart = pos + 1;
  node.forEach((child, _offset, index) => {
    if (index === 0) {
      if (child.content.size) {
        keep(paragraph.create(null, child.content), childStart, child.nodeSize);
      }
    } else if (!isEmptyTextblock(child)) {
      keep(child, childStart, child.nodeSize);
    }
    childStart += child.nodeSize;
  });

  const $pos = state.doc.resolve(pos);
  const parent = $pos.parent;
  const index = $pos.index();
  const removesOnlyChild = !blocks.length && parent.childCount === 1;
  const content = Fragment.fromArray(
    removesOnlyChild ? [paragraph.create()] : blocks
  );
  if (!parent.canReplace(index, index + 1, content)) return false;

  if (dispatch) {
    const tr = state.tr.replaceWith(pos, pos + node.nodeSize, content);
    const selection =
      target !== null
        ? textSelection(state, tr.doc, target)
        : nearSelection(state, tr.doc.resolve(removesOnlyChild ? pos + 1 : pos), -1);
    dispatch(tr.setSelection(selection).scrollIntoView());
  }
  return true;
}

// Toolbar button: removes the accordion around the cursor (keeping its
// content), otherwise inserts one.
export const toggleAccordion = (
  state: EditorState,
  dispatch?: Dispatch
): boolean => {
  const found = findAccordion(state);
  return found
    ? unwrapAccordion(state, dispatch, found)
    : insertAccordion(state, dispatch);
};

// The title is a single line, so Enter moves into the body instead of
// splitting the summary and leaving an empty paragraph behind.
const exitSummary = (state: EditorState, dispatch?: Dispatch): boolean => {
  const { $from } = state.selection;
  if ($from.parent.type.name !== "details_summary") return false;

  if (dispatch) {
    const bodyStart = nearSelection(state, state.doc.resolve($from.after()));
    dispatch(state.tr.setSelection(bodyStart).scrollIntoView());
  }
  return true;
};

// ProseMirror's default Backspace treats accordions like any other block,
// which selects or deletes the paragraph above, pulls the next paragraph
// into the body, or leaves an empty accordion that cannot be removed.
const accordionBackspace = (
  state: EditorState,
  dispatch?: Dispatch
): boolean => {
  const { selection } = state;
  const { $from } = selection;
  if (!selection.empty || $from.parentOffset > 0 || $from.depth < 1) {
    return false;
  }

  const container = $from.node($from.depth - 1);
  const index = $from.index($from.depth - 1);

  // Start of the title: remove the accordion, keep its content.
  if ($from.parent.type.name === "details_summary") {
    return unwrapAccordion(state, dispatch, {
      node: container,
      pos: $from.before($from.depth - 1)
    });
  }

  // Start of the first body line: never merge it into the title. An accordion
  // with no content left is deleted; otherwise the cursor moves to the title.
  if (container.type.name === "details" && index === 1) {
    const accordion = { node: container, pos: $from.before($from.depth - 1) };
    let empty = true;
    container.forEach((child) => {
      if (child.content.size) empty = false;
    });
    if (empty) return unwrapAccordion(state, dispatch, accordion);

    if (dispatch) {
      const titleEnd = $from.before() - 1;
      dispatch(state.tr.setSelection(textSelection(state, state.doc, titleEnd)));
    }
    return true;
  }

  // Start of the block right after an accordion: select the accordion (it is
  // outlined) so a second Backspace deletes it, instead of merging this block
  // into its body.
  const previous = index > 0 ? container.child(index - 1) : null;
  if (previous?.type.name === "details") {
    if (dispatch) {
      const accordionPos = $from.before() - previous.nodeSize;
      dispatch(
        state.tr.setSelection(
          selectionClass(state).fromJSON(state.doc, {
            type: "node",
            anchor: accordionPos
          })
        )
      );
    }
    return true;
  }

  return false;
};

// ngx-editor adds user plugins after its own keymaps, so its Enter/Backspace
// handlers would always win. Its shortcuts are turned off and re-added after
// ours.
export const accordionEditorOptions = () => ({
  schema: accordionSchema,
  nodeViews: accordionNodeViews,
  keyboardShortcuts: false,
  plugins: [
    // The keymap is typed against the root prosemirror-state; the commands
    // receive the editor's own state at runtime.
    keymap({
      Enter: exitSummary,
      Backspace: accordionBackspace
    } as unknown as Parameters<typeof keymap>[0]) as unknown as Plugin,
    ...getKeyboardShortcuts(accordionSchema, { history: true })
  ]
});

// Wraps the selected blocks in an accordion, or inserts an empty one at the
// cursor, then selects the placeholder title so typing replaces it.
export const insertAccordion = (
  state: EditorState,
  dispatch?: Dispatch
): boolean => {
  const { details: detailsType, details_summary, paragraph } =
    state.schema.nodes;
  const { selection } = state;
  const summary = details_summary.create(null, state.schema.text(ACCORDION_TITLE));

  if (!dispatch) return true;

  const tr = state.tr;
  let insertPos = -1;

  const range = !selection.empty && selection.$from.blockRange(selection.$to);
  const wrapped =
    range &&
    range.parent.canReplaceWith(range.startIndex, range.endIndex, detailsType)
      ? createDetails(detailsType, summary, blocksInRange(range))
      : null;

  if (range && wrapped) {
    tr.replaceWith(range.start, range.end, wrapped);
    insertPos = range.start;
  } else {
    // Insert after the selection rather than over it, so selected text that
    // could not be wrapped is kept.
    const accordion = detailsType.create(null, [summary, paragraph.create()]);
    const { $to } = selection;
    const parentIndex = $to.depth > 0 ? $to.index($to.depth - 1) : -1;

    if (
      selection.empty &&
      $to.parent.isTextblock &&
      $to.parent.content.size === 0 &&
      parentIndex >= 0 &&
      $to
        .node($to.depth - 1)
        .canReplaceWith(parentIndex, parentIndex + 1, detailsType)
    ) {
      // Cursor in an empty paragraph: replace it instead of leaving it behind.
      insertPos = $to.before();
      tr.replaceWith(insertPos, $to.after(), accordion);
    } else {
      let point = insertPoint(tr.doc, $to.pos, detailsType);
      if (point == null && canSplit(tr.doc, $to.pos)) {
        // Mid-paragraph: split it and put the accordion between the halves.
        tr.split($to.pos);
        point = insertPoint(tr.doc, $to.pos + 1, detailsType);
      }
      if (point == null) return false;
      tr.insert(point, accordion);
      insertPos = point;
    }
  }

  if (insertPos < 0) return false;

  // Leave a paragraph after the accordion so the cursor can get out of it.
  const inserted = tr.doc.nodeAt(insertPos);
  const after = insertPos + inserted.nodeSize;
  if (!tr.doc.resolve(after).nodeAfter) {
    tr.insert(after, paragraph.create());
  }

  const titleStart = insertPos + 2;
  tr.setSelection(
    textSelection(state, tr.doc, titleStart, titleStart + ACCORDION_TITLE.length)
  );
  dispatch(tr.scrollIntoView());
  return true;
};

function blocksInRange(range: {
  parent: ProseMirrorNode;
  startIndex: number;
  endIndex: number;
}): ProseMirrorNode[] {
  const blocks: ProseMirrorNode[] = [];
  for (let i = range.startIndex; i < range.endIndex; i++) {
    blocks.push(range.parent.child(i));
  }
  return blocks;
}

function createDetails(
  detailsType: ProseMirrorNode["type"],
  summary: ProseMirrorNode,
  blocks: ProseMirrorNode[]
): ProseMirrorNode | null {
  try {
    return detailsType.createChecked(null, [summary, ...blocks]);
  } catch {
    return null;
  }
}
